import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, FileText, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Schnellauswahl-Optionen
const PERIOD_OPTIONS = [
  {
    key: 'last3days',
    label: 'Letzte 3 Tage',
    getDates: () => {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 2);
      return { start: from.toISOString().split('T')[0], end: to.toISOString().split('T')[0] };
    },
  },
  {
    key: 'last7days',
    label: 'Letzte Woche',
    getDates: () => {
      const to = new Date();
      const from = new Date(); from.setDate(from.getDate() - 6);
      return { start: from.toISOString().split('T')[0], end: to.toISOString().split('T')[0] };
    },
  },
  {
    key: 'thismonth',
    label: 'Dieser Monat',
    getDates: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: from.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
    },
  },
  {
    key: 'lastmonth',
    label: 'Letzter Monat',
    getDates: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start: from.toISOString().split('T')[0], end: to.toISOString().split('T')[0] };
    },
  },
  { key: 'custom', label: 'Frei auswählbar', getDates: null },
];

export const TemperatureExport = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('thismonth');
  const [formData, setFormData] = useState({
    location_id: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchLocations();
    // Standard: Dieser Monat
    applyPeriod('thismonth');
  }, []);

  const applyPeriod = (key) => {
    setSelectedPeriod(key);
    const opt = PERIOD_OPTIONS.find(o => o.key === key);
    if (opt?.getDates) {
      const { start, end } = opt.getDates();
      setFormData(prev => ({ ...prev, start_date: start, end_date: end }));
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/admin/locations`);
      setLocations(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, location_id: response.data[0].id }));
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!formData.location_id) {
      toast.error('Bitte wählen Sie einen Standort aus');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error('Bitte wählen Sie einen Datumsbereich aus');
      return;
    }

    setExporting(true);

    try {
      const response = await axios.get(`${API}/admin/temperature/export`, {
        params: {
          location_id: formData.location_id,
          start_date: formData.start_date,
          end_date: formData.end_date
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const location = locations.find(loc => loc.id === formData.location_id);
      const filename = `Temperatur-Liste_${location?.name || 'Export'}_${formData.start_date}_${formData.end_date}.pdf`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF erfolgreich heruntergeladen!');
    } catch (error) {
      toast.error('Fehler beim Exportieren der Daten');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Manuelle Datumsänderung → auf "Frei auswählbar" wechseln
    if (field === 'start_date' || field === 'end_date') {
      setSelectedPeriod('custom');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="temperature-export-title">
            Temperatur-Listen Export
          </h1>
          <p className="text-muted-foreground mt-2">Exportieren Sie Temperatur-Daten als PDF</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              PDF Export
            </CardTitle>
            <CardDescription>
              Wählen Sie Standort und Zeitraum für den Export aus
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="location">Standort auswählen *</Label>
              <select
                id="location"
                value={formData.location_id}
                onChange={(e) => handleChange('location_id', e.target.value)}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="location-select"
              >
                {locations.map(location => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </div>

            {/* Schnellauswahl Zeitraum */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Zeitraum auswählen *
              </Label>
              <div className="flex flex-wrap gap-2">
                {PERIOD_OPTIONS.map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => applyPeriod(opt.key)}
                    data-testid={`period-${opt.key}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      selectedPeriod === opt.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-background border-border text-muted-foreground hover:border-blue-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Datumsfelder – immer sichtbar (manuell editierbar, bei Schnellauswahl vorgefüllt) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date" className="flex items-center gap-2 text-xs text-muted-foreground">
                  Von Datum *
                </Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleChange('start_date', e.target.value)}
                  required
                  data-testid="start-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Bis Datum *
                </Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => handleChange('end_date', e.target.value)}
                  required
                  data-testid="end-date-input"
                />
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Export-Inhalt:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Alle Temperatur-Messungen im gewählten Zeitraum</li>
                    <li>Messpunkt, Datum, Uhrzeit, Temperatur, Status</li>
                    <li>Mitarbeiter-Name und Kommentare</li>
                    <li>Alarm-Kennzeichnung bei Abweichungen</li>
                  </ul>
                </div>
              </div>
            </div>

            <Button
              onClick={handleExport}
              disabled={exporting || !formData.location_id}
              className="w-full"
              size="lg"
              data-testid="export-button"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Exportiere...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  PDF herunterladen
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle>Hinweise</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-md">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium">Digitale Archivierung</p>
                <p className="text-muted-foreground">Die PDF-Datei enthält alle Messungen mit Zeitstempel und ist revisionssicher.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-md">
                <Download className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-sm">
                <p className="font-medium">Druckbar</p>
                <p className="text-muted-foreground">Das PDF kann direkt gedruckt und physisch archiviert werden.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
